import React, { useCallback, useMemo } from 'react';
import { GeckoModal, GECKO_CONSTS, GeckoModalService } from 'ui';
import Loader from 'components/Loader';

import useChallenge from './useChallenge';
import SalesforceEnvironmentField from './SalesforceEnvironmentField';
import SalesforceAuthorizationCodeField from './SalesforceAuthorizationCodeField';
import EllucianAPIDomainField from './EllucianAPIDomainField';
import EllucianAPIKeyField from './EllucianAPIKeyField';
import GeckoLocation from 'services/gecko-location';
import GeckoErrorsAlert from 'components/GeckoErrorsAlert';

const ChallengeFormWrapper = ({
  'aria-label': ariaLabel,
  value,
  onSubmit,
  children,
}) => {
  const submitWrapper = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      return onSubmit(value);
    },
    [value, onSubmit],
  );

  return (
    <form aria-label={ariaLabel} onSubmit={submitWrapper}>
      {children}
    </form>
  );
};

const ChallengeForm = ({ challenge, onSubmit, value, setValue }) => {
  const { challengeName, challengeValue } = challenge;
  const ChallengeField = useMemo(() => {
    switch (challengeName) {
      case 'SalesforceEnvironment':
        return SalesforceEnvironmentField;
      case 'SalesforceAuthorizationCode':
        return SalesforceAuthorizationCodeField;
      case 'EllucianAPIDomain':
        return EllucianAPIDomainField;
      case 'EllucianAPIKey':
        return EllucianAPIKeyField;
      default:
        throw new Error(`Unknown challenge type "${challengeName}"`);
    }
  }, [challengeName]);
  const challengeLabel = useMemo(() => {
    switch (challengeName) {
      case 'SalesforceEnvironment':
        return 'Environment';
      case 'SalesforceAuthorizationCode':
        return 'Authenticate with Salesforce';
      case 'EllucianAPIDomain':
        return 'Ellucian Ethos API Domain';
      case 'EllucianAPIKey':
        return 'Ellucian Ethos API Key';
      default:
        throw new Error(`Unknown challenge type "${challengeName}"`);
    }
  }, [challengeName]);
  return (
    <ChallengeFormWrapper
      aria-label={challengeLabel}
      value={value}
      onSubmit={onSubmit}
    >
      <ChallengeField
        challengeValue={challengeValue}
        value={value}
        setValue={setValue}
        onSubmit={onSubmit}
      />
    </ChallengeFormWrapper>
  );
};

const GeckoIntegrationSetupModal = ({
  integrationId,
  integrationType,
  GeckoIntegrations,
}) => {
  const {
    value,
    setValue,
    isFetchingNextChallenge,
    nextChallenge,
    isSubmittingChallengeResponse,
    submitChallengeResponse,
    submissionErrors,
  } = useChallenge({ integrationId, GeckoIntegrations });

  const nextButton = useMemo(() => {
    return {
      name: 'Next',
      type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
      handler: () => submitChallengeResponse(value),
      disabledWhen: () => !value,
    };
  }, [value, submitChallengeResponse]);

  const finishButton = useMemo(() => {
    return {
      name: 'Finish',
      type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
      handler: () => {
        new GeckoModalService().close();
        return GeckoLocation.goTo(`/settings/integrations/${integrationId}`);
      },
    };
  }, [integrationId]);

  const modalButtons = useMemo(() => {
    if (nextChallenge !== null) {
      return [nextButton];
    }
    return [finishButton];
  }, [nextChallenge, nextButton, finishButton]);

  return (
    <GeckoModal
      id={'GeckoIntegrationSetupModal'}
      name={`Set Up ${integrationType} Integration`}
      btns={modalButtons}
    >
      <Loader
        isLoading={isFetchingNextChallenge || isSubmittingChallengeResponse}
      >
        <GeckoErrorsAlert name="Submission errors" errors={submissionErrors} />
        {nextChallenge ? (
          <ChallengeForm
            challenge={nextChallenge}
            onSubmit={submitChallengeResponse}
            value={value}
            setValue={setValue}
          />
        ) : null}
        {!nextChallenge ? (
          <div className="ConfirmSetLive">
            <div className="ConfirmSetLive-all-good">
              <span className="header">
                It looks like your integration is ready to go. Good Job!
              </span>
              {integrationType !== 'Ellucian' ? 'You can press Finish to start mapping your fields' : null}
            </div>
          </div>
        ) : null}
      </Loader>
    </GeckoModal>
  );
};

export default GeckoIntegrationSetupModal;
